import { MAX_INT } from '@lyra/core/constants/contracts'
import { useMemo } from 'react'
import useSWR from 'swr'
import { Address } from 'viem'

import erc20Abi from '../abis/erc20Abi'
import lyraOftWrapperAbi from '../abis/lyraOftWrapperAbi'
import tsaShareHandlerWithdrawAbi from '../abis/tsaShareHandlerWithdrawAbi'
import withdrawControllerAbi from '../abis/withdrawControllerAbi'
import { layerZeroWithdrawHelperAddress } from '../constants/bridge'
import { DepositNetwork } from '../constants/chains'
import { lyraClient } from '../constants/client'
import { DepositTokenId, TokenId } from '../constants/tokens'
import {
  fetchWithdrawFee,
  getDepositContractAddresses,
  getEidForDepositNetwork,
  getWithdrawContractAddresses,
} from '../utils/bridge'
import { getNetworkClient } from '../utils/rpc'
import { getDepositTokenAddress, getLyraTokenAddress } from '../utils/tokens'
import useAuth from './useAuth'

type WithdrawQuote = {
  fee: bigint
  isSufficientLiquidity: boolean
  isSufficientWithdrawLimit: boolean
}

const REFRESH_INTERVAL_MS = 30_000

const fetchLayerZeroQuote = async (
  withdrawNetwork: DepositNetwork,
  token: TokenId,
  amount: bigint
) => {
  const tokenAddress = getLyraTokenAddress(token)
  const eid = getEidForDepositNetwork(withdrawNetwork)

  const fee = await lyraClient.readContract({
    abi: lyraOftWrapperAbi,
    address: layerZeroWithdrawHelperAddress,
    functionName: 'getFeeInToken',
    args: [tokenAddress, amount, eid],
  })

  return {
    fee,
    vaultBalance: BigInt(MAX_INT),
    withdrawLimit: BigInt(MAX_INT),
  }
}

const fetcher = async (
  ownerAddress: Address,
  withdrawNetwork: DepositNetwork,
  token: TokenId,
  withdrawToken: DepositTokenId,
  amount: bigint
): Promise<{ fee: bigint; vaultBalance: bigint; withdrawLimit: bigint }> => {
  const contractAddresses = getDepositContractAddresses(withdrawNetwork, withdrawToken)

  if (contractAddresses.type === 'layer-zero') {
    return await fetchLayerZeroQuote(withdrawNetwork, token, amount)
  }

  const networkClient = await getNetworkClient(withdrawNetwork)

  const { socketVault } = contractAddresses
  const { socketController, socketWithdrawFastConnector, isNewBridge, shareHandlerWithdrawHook } =
    getWithdrawContractAddresses(withdrawNetwork, withdrawToken)
  const withdrawTokenAddress = getDepositTokenAddress(withdrawNetwork, withdrawToken)

  const [fee, vaultBalance, withdrawLimit] = await Promise.all([
    fetchWithdrawFee(ownerAddress, withdrawNetwork, token, withdrawToken),
    networkClient.readContract({
      address: withdrawTokenAddress,
      abi: erc20Abi,
      functionName: 'balanceOf',
      args: [socketVault],
    }),
    isNewBridge
      ? lyraClient.readContract({
          abi: tsaShareHandlerWithdrawAbi,
          address: shareHandlerWithdrawHook,
          functionName: 'getCurrentSendingLimit',
          args: [socketWithdrawFastConnector],
        })
      : lyraClient.readContract({
          abi: withdrawControllerAbi,
          address: socketController,
          functionName: 'getCurrentBurnLimit',
          args: [socketWithdrawFastConnector],
        }),
  ])

  return {
    fee,
    vaultBalance,
    withdrawLimit,
  }
}

export default function useWithdrawQuote(
  network: DepositNetwork,
  token: TokenId,
  withdrawToken: DepositTokenId,
  withdrawAmount: bigint
) {
  const { user } = useAuth()
  const ownerAddress = user?.ownerAddress
  const isLayerZeroQuote = getDepositContractAddresses(network, withdrawToken).type === 'layer-zero'
  const { data, isLoading, error } = useSWR(
    ownerAddress
      ? [
          'WithdrawQuote',
          ownerAddress,
          network,
          token,
          withdrawToken,
          isLayerZeroQuote ? withdrawAmount : BigInt(0),
        ]
      : null,
    ([_, ownerAddress, network, token, withdrawToken, withdrawAmount]) =>
      fetcher(ownerAddress, network, token, withdrawToken, withdrawAmount),
    { refreshInterval: REFRESH_INTERVAL_MS }
  )

  const quote: WithdrawQuote | undefined = useMemo(() => {
    if (data) {
      return {
        fee: data.fee,
        isSufficientLiquidity: data.vaultBalance >= withdrawAmount,
        isSufficientWithdrawLimit: data.withdrawLimit >= withdrawAmount,
      }
    }
  }, [data, withdrawAmount])

  return useMemo(
    () => ({ data: quote, isLoading, error: error as Error }),
    [quote, isLoading, error]
  )
}
